import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RichTextField } from './RichTextField';
import { Plus, X } from 'lucide-react';

interface HighlightItem {
  id?: string | number;
  title: string;
  titleLink?: string;
  description: string;
}

interface HighlightsListInputProps {
  value?: HighlightItem[];
  onChange: (_items: HighlightItem[]) => void;
}

export const HighlightsListInput: React.FC<HighlightsListInputProps> = ({
  value = [],
  onChange
}) => {
  const [items, setItems] = useState<HighlightItem[]>(value);

  useEffect(() => {
    setItems(value);
  }, [value]);

  const addItem = () => {
    const newItem: HighlightItem = {
      id: crypto.randomUUID(),
      title: '',
      titleLink: '',
      description: ''
    };
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    onChange(updatedItems);
  };

  const removeItem = (index: number) => {
    const updatedItems = items.filter((_, currentIndex) => currentIndex !== index);
    setItems(updatedItems);
    onChange(updatedItems);
  };

  const updateItem = <K extends keyof HighlightItem>(index: number, field: K, fieldValue: HighlightItem[K]) => {
    const updatedItems = items.map((item, currentIndex) =>
      currentIndex === index ? { ...item, [field]: fieldValue } : item
    );
    setItems(updatedItems);
    onChange(updatedItems);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addItem}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Adicionar Item
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-4 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Nenhum item adicionado
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={item.id ?? index} className="border border-gray-200 rounded-lg p-4 bg-white space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700">Item {index + 1}</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeItem(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <Input
                placeholder="Título do item"
                value={item.title}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  updateItem(index, 'title', event.target.value)
                }
              />
              <Input
                type="url"
                placeholder="Link do título (URL)"
                value={item.titleLink ?? ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  updateItem(index, 'titleLink', event.target.value)
                }
              />
              <RichTextField
                label="Descrição"
                value={item.description}
                onChange={(value: string) => updateItem(index, 'description', value)}
                placeholder="Descrição do item"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

